import { keyframes, css } from '@emotion/react';
import styled from '@emotion/styled';
import type { OverlayEffectStyleCustomizationRequest } from '@zola/svc-web-api-ts-client';
import { SPACING } from '@zola/zola-ui/src/styles/emotion';

const show = keyframes`
0% { 
  opacity: 0;
}

100% {
  opacity: 1;
}
`;

export const StyledOverlayWrapper = styled.div<{
  delay: number;
  isSinglePage?: boolean;
  heightPercentage: number;
  position?: OverlayEffectStyleCustomizationRequest.PositionEnum;
  isMonogram?: boolean;
}>`
  z-index: 101; // hero z-index is 100
  animation: ${show} ${props => props.delay}s;
  position: absolute;
  height: 100%;
  display: flex;
  flex-direction: column;
  top: ${props => (props.isSinglePage ? 0 : 'auto')};
  width: ${props => (props.isSinglePage ? '100%' : 'auto')};
  padding: ${SPACING.MD};
  
  ${props =>
    props.isSinglePage &&
    css`
      @supports (-webkit-hyphens: none) {
        position: revert;
        padding: 0;
      }
    `}
  path { 
    fill: inherit;
    stroke: ${props => (props.isMonogram ? '' : 'inherit')};
  }

   > div {
    height: ${props => props.heightPercentage}%;

    @supports (-webkit-hyphens: none) {

    ${props =>
      props.isSinglePage &&
      css`
        position: absolute;
        width: 100%;
      `}
    }
  }

  svg { 
      width: ${props => (props.isSinglePage ? '100%' : 'auto !important')};

      @supports (-webkit-hyphens: none) {
        width: auto !important;
        max-width: 100%;
     }
  }
 
  ${props =>
    (props.position as unknown) === 'TOP_LEFT' &&
    css`
      left: 0;
      top: 0;
      align-items: ${props.isSinglePage ? 'flex-start' : 'auto'};
      @supports (-webkit-hyphens: none) {
        > div {
          top: ${props.isSinglePage ? '0' : 'auto'};
          text-align: ${props.isSinglePage ? 'left' : 'auto'};
        }
      }
    `}

  ${props =>
    (props.position as unknown) === 'TOP_RIGHT' &&
    css`
      right: 0;
      top: 0;
      align-items: ${props.isSinglePage ? 'flex-end' : 'auto'};
      @supports (-webkit-hyphens: none) {
        > div {
          top: ${props.isSinglePage ? '0' : 'auto'};
          text-align: ${props.isSinglePage ? 'right' : 'auto'};
        }
      }
    `}

    ${props =>
      (props.position as unknown) === 'TOP_CENTER' &&
      css`
        @supports (-webkit-hyphens: none) {
          > div {
            top: ${props.isSinglePage ? '0' : 'auto'};
            text-align: ${props.isSinglePage ? 'center' : 'auto'};
          }
        }
      `}

  ${props =>
    (props.position as unknown) === 'LEFT' &&
    css`
      left: 0;
      justify-content: center;
      align-items: ${props.isSinglePage ? 'flex-start' : 'auto'};
      @supports (-webkit-hyphens: none) {
        > div {
          top: ${props.isSinglePage ? '25%' : 'auto'};
          text-align: ${props.isSinglePage ? 'left' : 'auto'};
        }
      }
    `}

    ${props =>
      (props.position as unknown) === 'CENTER' &&
      css`
        right: auto !important;
        left: auto;

        justify-content: center;

        @supports (-webkit-hyphens: none) {
          > div {
            top: ${props.isSinglePage ? '25%' : 'auto'};
            text-align: ${props.isSinglePage ? 'center' : 'auto'};
          }
        }
      `}

    ${props =>
      (props.position as unknown) === 'RIGHT' &&
      css`
        right: 0;
        justify-content: center;
        align-items: ${props.isSinglePage ? 'flex-end' : 'revert'};

        @supports (-webkit-hyphens: none) {
          > div {
            top: ${props.isSinglePage ? '25%' : 'auto'};
            text-align: ${props.isSinglePage ? 'right' : 'auto'};
          }
        }
      `}

  ${props =>
    (props.position as unknown) === 'BOTTOM_LEFT' &&
    css`
      left: 0;
      justify-content: end;
      align-items: ${props.isSinglePage ? 'flex-start' : 'revert'};
      @supports (-webkit-hyphens: none) {
        > div {
          bottom: ${props.isSinglePage ? '0' : 'auto'};
          text-align: ${props.isSinglePage ? 'left' : 'auto'};
        }
      }
    `}


  ${props =>
    (props.position as unknown) === 'BOTTOM_CENTER' &&
    css`
      justify-content: end;
      @supports (-webkit-hyphens: none) {
        > div {
          bottom: ${props.isSinglePage ? '0' : 'auto'};
          text-align: ${props.isSinglePage ? 'center' : 'auto'};
        }
      }
    `}


  ${props =>
    (props.position as unknown) === 'BOTTOM_RIGHT' &&
    css`
      right: 0;
      justify-content: end;
      align-items: ${props.isSinglePage ? 'flex-end' : 'revert'};
      @supports (-webkit-hyphens: none) {
        > div {
          bottom: ${props.isSinglePage ? '0' : 'auto'};
          text-align: ${props.isSinglePage ? 'right' : 'auto'};
        }
      }
    `}
`;

export const StyledBridlewoodWrapper = styled(StyledOverlayWrapper)`
  width: ${props => props.heightPercentage}%;
  padding: ${SPACING.MD} 0;
  height: auto;
  svg {
    width: 100% !important;
  }

  @supports (-webkit-hyphens: none) {
      > div {
        padding: ${SPACING.MD} 0;
      }
    }
  

  ${props =>
    props.isSinglePage &&
    css`
      width: 100%;
      height: 100%;

      > div {
        width: ${props.heightPercentage}%;
        height: auto;
      }

      ${((props.position as unknown) === 'BOTTOM_CENTER' ||
        (props.position as unknown) === 'CENTER' ||
        (props.position as unknown) === 'TOP_CENTER') &&
      css`
        align-items: center;
      `}
      ${((props.position as unknown) === 'CENTER' ||
        (props.position as unknown) === 'LEFT' ||
        (props.position as unknown) === 'RIGHT') &&
      css`
        > div {
          top: 43%;
        }
      `}
    `}


  ${props =>
    (props.position as unknown) === 'TOP_CENTER' &&
    css`
      top: 0;
    `}

  ${props =>
    ((props.position as unknown) === 'BOTTOM_LEFT' ||
      (props.position as unknown) === 'BOTTOM_CENTER' ||
      (props.position as unknown) === 'BOTTOM_RIGHT') &&
    css`
      bottom: 20px;
    `}
`;

export const StyledGlenardenWrapper = styled.div<{
  delay: number;
  isSinglePage?: boolean;
  heightPercentage: number;
}>`
  z-index: 101; // hero z-index is 100
  animation: ${show} ${props => props.delay}s;
  position: absolute;
  bottom: 20px;
  width: ${props => props.heightPercentage}%;

  display: flex;
  justify-content: center;
  height: auto;

  ${props =>
    props.isSinglePage &&
    css`
      width: 100%;
      > div {
        width: ${props.heightPercentage}%;
      }
    `}
`;

export const StyledViallTopRightWrapper = styled(StyledOverlayWrapper)`
  right: 0;

  ${props =>
    props.isSinglePage &&
    css`
      width: auto;
      @supports (-webkit-hyphens: none) {
        > div {
          top: 0;
          right: 0;
          width: auto !important;
        }
      }
    `}
`;

export const StyledViallBottomLeftWrapper = styled(StyledOverlayWrapper)`
  left: 0;

  justify-content: flex-end;

  ${props =>
    props.isSinglePage &&
    css`
      width: auto;
      @supports (-webkit-hyphens: none) {
        > div {
          bottom: 0;
          left: 0;
          width: auto !important;
        }
      }
    `}
`;
